import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import {
  useSpecimens,
  useActiveDiagnosticCategories,
  useScanSpecimen,
  useCollectSpecimen,
  useReceiveSpecimen,
  useProcessSpecimen,
  useCompleteSpecimen,
  useRejectSpecimen,
  useRedrawSpecimen,
  useDeleteSpecimen,
} from '../../services/httpServices/queries';
import { diagnosticService } from '../../services/httpServices/diagnosticService';
import type { LabSpecimen } from '../../services/httpServices/diagnosticService';
import { specimenCategoryNames } from '../../utils/specimen';
import { joinFullName } from '../../utils/patientName';
import { useDebounce } from '../../hooks/useDebounce';
import { usePermissions } from '../../hooks/usePermissions';
import { useListKeyboardNav } from '../../hooks/useListKeyboardNav';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Badge } from '../../components/ui/badge';
import { Pagination } from '../../components/ui/Pagination';
import { SpecimenDetailsModal } from '../../components/ui/SpecimenDetailsModal';
import { getLabelPrinter, getReceiptPrinter, isRemoteHost } from '../../utils/devices';
import { getLabelPrinterShareName, getLabelPrinterHost } from '../../utils/printerShare';
import {
  agentRawPrint,
  agentPrintDocument,
  blobToBase64,
  getPrintAgentIdentities,
  isPrintAgentAvailable,
} from '../../utils/printAgent';
import { enqueueDocumentBase64 } from '../../utils/printJobs';
import { emitPrintRequest } from '../../utils/printSocket';
import { notifyPrintAgentOffline } from '../../utils/printAgentOffline';
import { toast } from 'sonner';
import {
  ScanBarcode,
  Printer,
  CheckCircle,
  XCircle,
  Loader2,
  AlertCircle,
  Search,
  FlaskConical,
  RefreshCw,
  User,
  FilterX,
  RotateCcw,
  Trash2,
} from 'lucide-react';

const statusMeta: Record<
  string,
  { label: string; variant: 'warning' | 'secondary' | 'primary' | 'success' | 'danger' | 'default' }
> = {
  PENDING: { label: 'Pending', variant: 'warning' },
  COLLECTED: { label: 'Collected', variant: 'primary' },
  RECEIVED: { label: 'Received', variant: 'secondary' },
  PROCESSING: { label: 'Processing', variant: 'primary' },
  COMPLETED: { label: 'Completed', variant: 'success' },
  REJECTED: { label: 'Rejected', variant: 'danger' },
  CANCELLED: { label: 'Cancelled', variant: 'danger' },
};

function specimenTests(specimen: LabSpecimen): string {
  return (specimen.items || [])
    .map((i) => i.receiptItem?.testName)
    .filter(Boolean)
    .join(', ');
}

function ageLabel(createdAt: string): string {
  const mins = Math.max(0, Math.floor((Date.now() - new Date(createdAt).getTime()) / 60000));
  if (mins < 60) return `${mins} min`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours} h`;
  return `${Math.floor(hours / 24)} d`;
}

export function CollectionStation() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { hasPermission } = usePermissions();
  // The printer designated in Settings → Devices for collection labels. When
  // present, labels are auto-printed with no browser dialog to pick a printer.
  const labelPrinter = getLabelPrinter();

  const [receiptBarcode, setReceiptBarcode] = useState('');
  const [receiptLoading, setReceiptLoading] = useState(false);
  const [scannedReceipt, setScannedReceipt] = useState<any>(null);

  const [tubeBarcode, setTubeBarcode] = useState('');
  const [scannedSpecimen, setScannedSpecimen] = useState<LabSpecimen | null>(null);

  const [categoryId, setCategoryId] = useState('');
  const [search, setSearch] = useState('');
  const debouncedSearch = useDebounce(search, 350);
  const [queueStatus, setQueueStatus] = useState('PENDING');
  const [olderThanHours, setOlderThanHours] = useState('0');
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [page, setPage] = useState(1);
  const [detailsId, setDetailsId] = useState<string | null>(null);
  const limit = 20;

  const [rejectTarget, setRejectTarget] = useState<LabSpecimen | null>(null);
  const [rejectReason, setRejectReason] = useState('');
  const [deleteTarget, setDeleteTarget] = useState<LabSpecimen | null>(null);

  const { data: categoriesData } = useActiveDiagnosticCategories();
  const categories = categoriesData?.data || [];

  const queueFilters = useMemo(() => {
    const hours = Number(olderThanHours) || 0;
    return {
      status: queueStatus || undefined,
      categoryId: categoryId || undefined,
      search: debouncedSearch || undefined,
      dateFrom: hours > 0 ? new Date(Date.now() - hours * 60 * 60 * 1000).toISOString() : undefined,
    };
  }, [queueStatus, categoryId, debouncedSearch, olderThanHours]);
  const { data: queueData, isLoading: queueLoading, error: queueError } = useSpecimens(queueFilters, page, limit);
  const queue = queueData?.data || [];
  const meta = queueData?.meta;

  // Reset paging/selection whenever the queue filters change.
  useEffect(() => {
    setPage(1);
    setSelectedIds([]);
  }, [queueFilters]);

  const scanSpecimen = useScanSpecimen();
  const collectSpecimen = useCollectSpecimen();
  const receiveSpecimen = useReceiveSpecimen();
  const processSpecimen = useProcessSpecimen();
  const completeSpecimen = useCompleteSpecimen();
  const rejectSpecimen = useRejectSpecimen();
  const redrawSpecimen = useRedrawSpecimen();
  const deleteSpecimen = useDeleteSpecimen();

  const canCreate = hasPermission('diagnostics:specimen:create');
  const canPrint = hasPermission('diagnostics:specimen:print');
  const canCollect = hasPermission('diagnostics:specimen:collect');
  const canReceive = hasPermission('diagnostics:specimen:receive');
  const canProcess = hasPermission('diagnostics:specimen:process');
  const canComplete = hasPermission('diagnostics:specimen:complete');
  const canReject = hasPermission('diagnostics:specimen:reject');
  const canRedraw = hasPermission('diagnostics:specimen:redraw');
  const canDelete = hasPermission('diagnostics:specimen:delete');

  const handleScanReceipt = async () => {
    if (!receiptBarcode.trim()) return;
    setReceiptLoading(true);
    setScannedReceipt(null);
    try {
      const res = await diagnosticService.scanBarcode(receiptBarcode.trim());
      setScannedReceipt(res.data);
    } catch (err: any) {
      toast.error(err?.message || 'Receipt not found');
    } finally {
      setReceiptLoading(false);
    }
  };

  const handleScanTube = async () => {
    if (!tubeBarcode.trim()) return;
    try {
      const res = await scanSpecimen.mutateAsync(tubeBarcode.trim());
      setScannedSpecimen(res.data);
      setTubeBarcode('');
    } catch (err: any) {
      toast.error(err?.message || 'Specimen not found for this barcode');
    }
  };

  const refreshScannedSpecimen = (updated: LabSpecimen) => {
    setScannedSpecimen((prev) => (prev && prev.id === updated.id ? updated : prev));
  };

  const handleCollect = async (specimen: LabSpecimen) => {
    const res = await collectSpecimen.mutateAsync(specimen.id);
    toast.success(`Collected ${specimen.specimenBarcode}`);
    refreshScannedSpecimen(res.data);
  };

  const handleReceive = async (specimen: LabSpecimen) => {
    const res = await receiveSpecimen.mutateAsync(specimen.id);
    toast.success(`Received ${specimen.specimenBarcode}`);
    refreshScannedSpecimen(res.data);
  };

  const handleProcess = async (specimen: LabSpecimen) => {
    const res = await processSpecimen.mutateAsync(specimen.id);
    toast.success(`Processing ${specimen.specimenBarcode}`);
    refreshScannedSpecimen(res.data);
  };

  const handleComplete = async (specimen: LabSpecimen) => {
    const res = await completeSpecimen.mutateAsync(specimen.id);
    toast.success(`Completed ${specimen.specimenBarcode}`);
    refreshScannedSpecimen(res.data);
  };

  const handleRedraw = async (specimen: LabSpecimen) => {
    const res = await redrawSpecimen.mutateAsync(specimen.id);
    toast.success(`Re-drawn as ${res.data.specimenBarcode}`);
    setScannedSpecimen(res.data);
  };

  const confirmReject = async () => {
    if (!rejectTarget || !rejectReason.trim()) return;
    const res = await rejectSpecimen.mutateAsync({ id: rejectTarget.id, reason: rejectReason.trim() });
    toast.success(`Rejected ${rejectTarget.specimenBarcode}`);
    refreshScannedSpecimen(res.data);
    setRejectTarget(null);
    setRejectReason('');
  };

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    const target = deleteTarget;
    await deleteSpecimen.mutateAsync(target.id);
    toast.success(`Deleted ${target.specimenBarcode}`);
    // Drop the tube from the local receipt/scan panels; the queue refreshes via
    // query invalidation. The linked tests are freed, so "Generate Specimen
    // Plan" re-creates the tube (with a new barcode).
    setScannedSpecimen((prev) => (prev && prev.id === target.id ? null : prev));
    setScannedReceipt((prev: any) =>
      prev
        ? { ...prev, specimens: (prev.specimens || []).filter((s: LabSpecimen) => s.id !== target.id) }
        : prev,
    );
    setDeleteTarget(null);
  };

  const handlePrintLabels = async (specimens: LabSpecimen[]) => {
    const ids = specimens.map((s) => s.id);
    if (ids.length === 0) return;
    // Read fresh from localStorage so a printer just configured in Settings is
    // picked up without a reload.
    const share = getLabelPrinterShareName();
    const host = getLabelPrinterHost();
    // A "Regular Printer" device is a normal (non-thermal) printer whose driver
    // cannot accept raw ESC/POS — print a rendered PDF through the agent's
    // document path instead. Thermal/label printers keep the ESC/POS path.
    const printerDevice = getLabelPrinter() ?? getReceiptPrinter();
    const usesDocumentPrinter = printerDevice?.type === 'printer';
    // Labels for a printer on another PC go through the cloud relay (the agent
    // on that PC prints them), so SMB reachability from this workstation is not
    // required.
    const identity = await getPrintAgentIdentities(true);
    const remote = isRemoteHost(host, identity.hostname, identity.ipAddresses);
    try {
      const agentUp = await isPrintAgentAvailable(1200, true);

      // Preferred: the cloud backend renders the labels and THIS PC's agent
      // sends them to the printer — a local share directly, or a printer on
      // another PC over SMB (`\\host\share`). No agent or dashboard is needed on
      // the printer's own PC.
      if (agentUp) {
        const targetHost = remote ? host : undefined;
        try {
          if (usesDocumentPrinter) {
            const url = await diagnosticService.getSpecimenLabelsPdf(ids);
            try {
              const blob = await (await fetch(url)).blob();
              const base64 = await blobToBase64(blob);
              await agentPrintDocument(base64, 'pdf', share || undefined, targetHost);
            } finally {
              URL.revokeObjectURL(url);
            }
          } else {
            const rendered = await diagnosticService.renderSpecimenLabels(
              ids,
              share || undefined,
              targetHost,
            );
            await agentRawPrint(
              rendered.data.base64,
              rendered.data.shareName || share,
              targetHost,
            );
          }
          await diagnosticService.confirmSpecimenLabelsPrinted(ids);
          toast.success(
            `Sent ${ids.length} label(s) to ${printerDevice?.name || 'the shared printer'}`,
          );
          return;
        } catch (err) {
          // Local printer failure is handled by the outer catch (PDF dialog).
          // A remote SMB failure falls through to the cloud relay below.
          if (!remote) throw err;
        }
      }

      // Remote printer with no reachable local agent (or a failed direct SMB
      // send) — push the job to the dashboard on the printer's PC. Regular
      // printers get a rendered PDF; thermal/label printers get raw ESC/POS.
      if (usesDocumentPrinter) {
        const pdfUrl = await diagnosticService.getSpecimenLabelsPdf(ids);
        let delivered = false;
        try {
          const blob = await (await fetch(pdfUrl)).blob();
          const base64 = await blobToBase64(blob);
          const res = await enqueueDocumentBase64(
            base64,
            share,
            host,
            printerDevice?.name,
            'diagnostics.specimen.labels',
          );
          delivered = res.data?.delivered !== false;
        } finally {
          URL.revokeObjectURL(pdfUrl);
        }
        if (!delivered) {
          notifyPrintAgentOffline({
            host,
            shareName: share || undefined,
            printerName: printerDevice?.name || share,
          });
          return;
        }
        await diagnosticService.confirmSpecimenLabelsPrinted(ids);
        toast.success(
          `Sent ${ids.length} label(s) to ${printerDevice?.name || 'the shared printer'}`,
        );
        return;
      }

      // Real-time socket relay — the backend renders the labels and the agent on
      // the printer's PC prints them; print-count/audit is recorded server-side
      // when the job completes.
      const relay = await emitPrintRequest({
        shareName: share,
        host,
        source: 'diagnostics.specimen.labels',
        jobRef: JSON.stringify({ specimenIds: ids }),
      });
      if (relay?.ok) {
        if (relay.delivered === false) {
          notifyPrintAgentOffline({
            host,
            shareName: share || undefined,
            printerName: getLabelPrinter()?.name || share,
          });
          return;
        }
        toast.success(
          `Sent ${ids.length} label(s) to ${getLabelPrinter()?.name || 'the shared printer'}`,
        );
        return;
      }

      // Legacy REST relay.
      const res = await diagnosticService.printSpecimenLabels(ids, share || undefined, host);
      if (res.data?.delivered === false) {
        notifyPrintAgentOffline({
          host,
          shareName: share || undefined,
          printerName: getLabelPrinter()?.name || share,
        });
        return;
      }
      toast.success(
        `Sent ${res.data?.printed ?? ids.length} label(s) to ${getLabelPrinter()?.name || 'the shared printer'}`,
      );
    } catch (err: any) {
      toast.error(err?.message || 'Failed to print specimen labels');
      // Fall back to the PDF print dialog if direct printing is unavailable.
      try {
        const url = await diagnosticService.getSpecimenLabelsPdf(ids);
        window.open(url, '_blank');
      } catch {
        /* ignore — the error toast above already reported the failure */
      }
    }
  };

  // Manual print path — works without a scanner: find specimens by patient /
  // receipt / barcode in the queue, tick them, and print their labels.
  const handlePrintSelected = async () => {
    const items = queue.filter((s) => selectedIds.includes(s.id));
    if (items.length === 0) return;
    await handlePrintLabels(items);
    setSelectedIds([]);
  };

  const toggleSelect = (id: string) => {
    setSelectedIds((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));
  };

  const allQueueSelected = queue.length > 0 && queue.every((s) => selectedIds.includes(s.id));
  const toggleSelectAll = () => {
    setSelectedIds(allQueueSelected ? [] : queue.map((s) => s.id));
  };

  // Up/Down moves through the queue, Enter toggles the highlighted specimen's
  // selection (for label printing) — same keyboard flow as the other lists.
  const queueNav = useListKeyboardNav({
    itemCount: queue.length,
    onSelect: (index) => {
      const specimen = queue[index];
      if (specimen && canPrint) toggleSelect(specimen.id);
    },
    resetKey: `${search}|${queueStatus}|${categoryId}|${olderThanHours}|${page}`,
    enabled: canPrint,
  });

  const handleGeneratePlan = async (receiptId: string) => {
    try {
      const res = await diagnosticService.planReceiptSpecimens(receiptId);
      setScannedReceipt((prev: any) => (prev ? { ...prev, specimens: res.data } : prev));
      // Re-planning can create (or re-create) tubes, so refresh the queue.
      queryClient.invalidateQueries({ queryKey: ['specimens'] });
      toast.success('Specimen plan generated');
    } catch (err: any) {
      toast.error(err?.message || 'Failed to generate specimen plan');
    }
  };

  const scannedSpecimens: LabSpecimen[] = scannedReceipt?.specimens || [];
  const pendingScanned = scannedSpecimens.filter((s) => s.status === 'PENDING');

  const tubeActions: Record<string, { label: string; run: (s: LabSpecimen) => void; allowed: boolean }> = {
    PENDING: { label: 'Mark Collected', run: handleCollect, allowed: canCollect },
    COLLECTED: { label: 'Mark Received', run: handleReceive, allowed: canReceive },
    RECEIVED: { label: 'Start Processing', run: handleProcess, allowed: canProcess },
    PROCESSING: { label: 'Mark Completed', run: handleComplete, allowed: canComplete },
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-3">
        <div className="p-2 bg-primary/10 rounded-lg">
          <ScanBarcode className="w-5 h-5 text-primary" />
        </div>
        <div className="flex-1 min-w-0">
          <h1 className="text-xl font-bold text-text">Specimen Collection</h1>
          <p className="text-xs text-text-muted">
            Scan the receipt to print tube labels, then scan each tube to confirm collection.
          </p>
          {labelPrinter ? (
            <p className="text-xs text-text-muted mt-0.5">
              Labels print directly to <span className="font-medium text-text">{labelPrinter.name}</span>.{' '}
              <button
                type="button"
                onClick={() => navigate('/settings/devices')}
                className="text-primary hover:underline"
              >
                Change
              </button>
            </p>
          ) : (
            <p className="text-xs text-text-muted mt-0.5">
              Labels print to the default shared printer.{' '}
              <button
                type="button"
                onClick={() => navigate('/settings/devices')}
                className="text-primary hover:underline"
              >
                Set a specific printer in Devices
              </button>
              .
            </p>
          )}
        </div>
      </div>

      {/* Scan inputs */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="bg-surface border border-border rounded-xl p-4 space-y-3">
          <label className="block text-xs font-medium text-text-muted">Scan Receipt Barcode</label>
          <div className="flex gap-2">
            <Input
              placeholder="Scan or type receipt barcode..."
              value={receiptBarcode}
              onChange={(e) => setReceiptBarcode(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && handleScanReceipt()}
            />
            <Button onClick={handleScanReceipt} disabled={receiptLoading || !receiptBarcode.trim()}>
              {receiptLoading ? <Loader2 size={14} className="animate-spin" /> : <ScanBarcode size={14} />}
            </Button>
          </div>
        </div>

        <div className="bg-surface border border-border rounded-xl p-4 space-y-3">
          <label className="block text-xs font-medium text-text-muted">Scan Tube Barcode</label>
          <div className="flex gap-2">
            <Input
              placeholder="Scan or type specimen barcode..."
              value={tubeBarcode}
              onChange={(e) => setTubeBarcode(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && handleScanTube()}
            />
            <Button onClick={handleScanTube} disabled={scanSpecimen.isPending || !tubeBarcode.trim()}>
              {scanSpecimen.isPending ? <Loader2 size={14} className="animate-spin" /> : <ScanBarcode size={14} />}
            </Button>
          </div>
        </div>
      </div>

      {/* Receipt result */}
      {scannedReceipt && (
        <div className="bg-surface border border-border rounded-xl p-4 space-y-3">
          <div className="flex items-center justify-between flex-wrap gap-2">
            <div className="flex items-center gap-2">
              <FlaskConical size={16} className="text-primary" />
              <span className="text-sm font-semibold text-text">{scannedReceipt.receiptNumber}</span>
              <span className="text-xs text-text-muted">
                {joinFullName(scannedReceipt.patient || {})}
              </span>
            </div>
            <div className="flex items-center gap-2">
              {/* Always available when the user can plan: the plan is
                  incremental/idempotent, so "Generate" also re-creates a tube
                  that was just deleted (or picks up a test added later). */}
              {canCreate && (
                <Button variant="outline" size="sm" onClick={() => handleGeneratePlan(scannedReceipt.id)}>
                  <RefreshCw size={12} className="mr-1" /> Generate Specimen Plan
                </Button>
              )}
              {scannedSpecimens.length > 0 && canPrint && (
                <Button variant="outline" size="sm" onClick={() => handlePrintLabels(pendingScanned.length ? pendingScanned : scannedSpecimens)}>
                  <Printer size={12} className="mr-1" /> Print Specimen Labels
                </Button>
              )}
            </div>
          </div>

          {scannedSpecimens.length === 0 ? (
            <p className="text-xs text-text-muted">
              No specimens planned for this receipt yet. This is expected for procedure-only tests (ECG, X-ray).
            </p>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
              {scannedSpecimens.map((s) => (
                <div key={s.id} className="bg-background rounded-lg p-3 border border-border space-y-1">
                  <div className="flex items-center justify-between">
                    <span className="text-xs font-medium text-text">
                      {s.specimenContainerCode || 'Container'}
                      {s.containerColor ? ` · ${s.containerColor}` : ''}
                    </span>
                    <Badge variant={statusMeta[s.status]?.variant || 'default'}>{statusMeta[s.status]?.label || s.status}</Badge>
                  </div>
                  <p className="text-[0.6875rem] text-text-muted font-mono">{s.specimenBarcode}</p>
                  <p className="text-[0.6875rem] text-text-muted">{specimenTests(s)}</p>
                  {((s.status === 'PENDING' && canCollect) ||
                    ((s.status === 'PENDING' || s.status === 'CANCELLED') && canDelete)) && (
                    <div className="flex items-center gap-1 mt-1">
                      {s.status === 'PENDING' && canCollect && (
                        <Button size="sm" onClick={() => handleCollect(s)} disabled={collectSpecimen.isPending}>
                          <CheckCircle size={12} className="mr-1" /> Collect
                        </Button>
                      )}
                      {(s.status === 'PENDING' || s.status === 'CANCELLED') && canDelete && (
                        <Button
                          variant="outline"
                          size="sm"
                          className="text-danger hover:bg-danger/10 hover:text-danger"
                          onClick={() => setDeleteTarget(s)}
                          title="Delete specimen"
                        >
                          <Trash2 size={12} />
                        </Button>
                      )}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Tube scan result */}
      {scannedSpecimen && (
        <div className="bg-surface border border-primary/30 rounded-xl p-4 space-y-3">
          <div className="flex items-center justify-between flex-wrap gap-2">
            <div className="flex items-center gap-2">
              <User size={16} className="text-primary" />
              <span className="text-sm font-semibold text-text">
                {joinFullName(scannedSpecimen.patient || {})}
              </span>
              <Badge variant={statusMeta[scannedSpecimen.status]?.variant || 'default'}>
                {statusMeta[scannedSpecimen.status]?.label || scannedSpecimen.status}
              </Badge>
            </div>
            <span className="text-[0.6875rem] font-mono text-text-muted">{scannedSpecimen.specimenBarcode}</span>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-xs">
            <div>
              <p className="text-text-muted">Container</p>
              <p className="text-text font-medium">
                {scannedSpecimen.specimenContainerCode || 'N/A'}
                {scannedSpecimen.containerColor ? ` · ${scannedSpecimen.containerColor}` : ''}
              </p>
            </div>
            <div>
              <p className="text-text-muted">Category</p>
              <p className="text-text font-medium">{specimenCategoryNames(scannedSpecimen) || 'N/A'}</p>
            </div>
            <div>
              <p className="text-text-muted">Receipt</p>
              <p className="text-text font-medium font-mono">{(scannedSpecimen as any).receipt?.receiptNumber || 'N/A'}</p>
            </div>
            <div>
              <p className="text-text-muted">Tests</p>
              <p className="text-text font-medium">{specimenTests(scannedSpecimen) || 'N/A'}</p>
            </div>
          </div>

          <div className="flex flex-wrap items-center gap-2 pt-2 border-t border-border/60">
            {tubeActions[scannedSpecimen.status]?.allowed && (
              <Button size="sm" onClick={() => tubeActions[scannedSpecimen.status].run(scannedSpecimen)}>
                {tubeActions[scannedSpecimen.status].label}
              </Button>
            )}
            {scannedSpecimen.status === 'REJECTED' && canRedraw && (
              <Button size="sm" onClick={() => handleRedraw(scannedSpecimen)} disabled={redrawSpecimen.isPending}>
                <RotateCcw size={12} className="mr-1" /> Re-draw
              </Button>
            )}
            {scannedSpecimen.status !== 'COMPLETED' &&
              scannedSpecimen.status !== 'CANCELLED' &&
              scannedSpecimen.status !== 'REJECTED' &&
              canReject && (
                <Button variant="danger" size="sm" onClick={() => setRejectTarget(scannedSpecimen)}>
                  <XCircle size={12} className="mr-1" /> Reject
                </Button>
              )}
            {(scannedSpecimen.status === 'PENDING' || scannedSpecimen.status === 'CANCELLED') && canDelete && (
              <Button
                variant="outline"
                size="sm"
                className="text-danger hover:bg-danger/10 hover:text-danger"
                onClick={() => setDeleteTarget(scannedSpecimen)}
              >
                <Trash2 size={12} className="mr-1" /> Delete
              </Button>
            )}
            <Button variant="ghost" size="sm" onClick={() => navigate(`/diagnostics/receipts/${scannedSpecimen.receiptId}`)}>
              View Receipt
            </Button>
          </div>
        </div>
      )}

      {/* Queue */}
      <div className="bg-surface border border-border rounded-xl p-4 space-y-3">
        <div className="flex flex-wrap items-center gap-3">
          <div className="relative flex-1 min-w-[200px]">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-text-muted" />
            <Input
              placeholder="Search by barcode, receipt no., or patient..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              onKeyDown={queueNav.onKeyDown}
              className="pl-9"
            />
          </div>
          <select
            value={queueStatus}
            onChange={(e) => setQueueStatus(e.target.value)}
            className="px-3 py-2 border border-border rounded-lg bg-background text-text text-sm"
          >
            <option value="">All Status</option>
            <option value="PENDING">Pending</option>
            <option value="COLLECTED">Collected</option>
            <option value="RECEIVED">Received</option>
            <option value="PROCESSING">Processing</option>
            <option value="COMPLETED">Completed</option>
            <option value="REJECTED">Rejected</option>
          </select>
          <select
            value={categoryId}
            onChange={(e) => setCategoryId(e.target.value)}
            className="px-3 py-2 border border-border rounded-lg bg-background text-text text-sm"
          >
            <option value="">All Categories</option>
            {categories.map((c: any) => (
              <option key={c.id} value={c.id}>
                {c.name}
              </option>
            ))}
          </select>
          <select
            value={olderThanHours}
            onChange={(e) => setOlderThanHours(e.target.value)}
            className="px-3 py-2 border border-border rounded-lg bg-background text-text text-sm"
            title="Show only specimens older than the selected age"
          >
            <option value="0">Any age</option>
            <option value="1">Older than 1h</option>
            <option value="4">Older than 4h</option>
            <option value="24">Older than 24h</option>
            <option value="48">Older than 48h</option>
          </select>
          {canPrint && selectedIds.length > 0 && (
            <Button variant="outline" size="sm" onClick={handlePrintSelected}>
              <Printer size={14} className="mr-1" /> Print selected ({selectedIds.length})
            </Button>
          )}
          {(search || categoryId || queueStatus !== 'PENDING' || olderThanHours !== '0') && (
            <Button
              variant="ghost"
              size="sm"
              onClick={() => {
                setSearch('');
                setCategoryId('');
                setQueueStatus('PENDING');
                setOlderThanHours('0');
                setSelectedIds([]);
              }}
            >
              <FilterX size={14} className="mr-1" /> Clear
            </Button>
          )}
        </div>

        <p className="text-[0.6875rem] text-text-muted">
          No scanner? Search by receipt number or patient, tick the specimens, then use{' '}
          <span className="font-medium text-text">Print selected</span> to print their labels.
        </p>

        {queueError ? (
          <div className="flex items-center gap-2 p-3 bg-danger/10 border border-danger/20 rounded-lg text-danger text-sm">
            <AlertCircle size={16} /> Failed to load the collection queue
          </div>
        ) : queueLoading ? (
          <div className="flex items-center justify-center py-10">
            <Loader2 className="w-6 h-6 animate-spin text-primary" />
          </div>
        ) : queue.length === 0 ? (
          <p className="text-sm text-text-muted py-8 text-center">No specimens in this queue.</p>
        ) : (
          <div ref={queueNav.listRef} className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-border bg-background/50">
                  <th className="px-3 py-2 text-left w-8">
                    {canPrint && (
                      <input
                        type="checkbox"
                        checked={allQueueSelected}
                        onChange={toggleSelectAll}
                        aria-label="Select all specimens"
                        className="h-4 w-4 rounded border-border accent-primary"
                      />
                    )}
                  </th>
                  <th className="px-3 py-2 text-left text-xs font-medium text-text-muted uppercase">Specimen</th>
                  <th className="px-3 py-2 text-left text-xs font-medium text-text-muted uppercase">Patient</th>
                  <th className="px-3 py-2 text-left text-xs font-medium text-text-muted uppercase">Category</th>
                  <th className="px-3 py-2 text-left text-xs font-medium text-text-muted uppercase">Container</th>
                  <th className="px-3 py-2 text-left text-xs font-medium text-text-muted uppercase">Tests</th>
                  <th className="px-3 py-2 text-left text-xs font-medium text-text-muted uppercase">Age</th>
                  <th className="px-3 py-2 text-left text-xs font-medium text-text-muted uppercase">Status</th>
                  <th className="px-3 py-2 text-right text-xs font-medium text-text-muted uppercase">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {queue.map((s, index) => (
                  <tr
                    key={s.id}
                    data-nav-index={index}
                    onMouseEnter={() => queueNav.setHighlightedIndex(index)}
                    className={`transition-colors ${
                      index === queueNav.highlightedIndex
                        ? 'bg-background ring-1 ring-inset ring-primary/30'
                        : 'hover:bg-background/40'
                    }`}
                  >
                    <td className="px-3 py-2">
                      {canPrint && (
                        <input
                          type="checkbox"
                          checked={selectedIds.includes(s.id)}
                          onChange={() => toggleSelect(s.id)}
                          aria-label={`Select ${s.specimenBarcode}`}
                          className="h-4 w-4 rounded border-border accent-primary"
                        />
                      )}
                    </td>
                    <td className="px-3 py-2">
                      <button
                        type="button"
                        onClick={() => setDetailsId(s.id)}
                        className="text-xs font-mono text-primary hover:underline"
                        title="View specimen details"
                      >
                        {s.specimenBarcode}
                      </button>
                    </td>
                    <td className="px-3 py-2 text-sm text-text">
                      {joinFullName(s.patient || {})}
                    </td>
                    <td className="px-3 py-2 text-sm text-text-muted">{specimenCategoryNames(s) || '—'}</td>
                    <td className="px-3 py-2 text-sm text-text-muted">
                      {s.specimenContainerCode || '—'}
                      {s.containerColor ? ` · ${s.containerColor}` : ''}
                    </td>
                    <td className="px-3 py-2 text-xs text-text-muted">{specimenTests(s)}</td>
                    <td className="px-3 py-2">
                      <Badge variant={ageLabel(s.createdAt).endsWith('d') ? 'danger' : 'warning'}>
                        {ageLabel(s.createdAt)}
                      </Badge>
                    </td>
                    <td className="px-3 py-2">
                      <Badge variant={statusMeta[s.status]?.variant || 'default'}>
                        {statusMeta[s.status]?.label || s.status}
                      </Badge>
                    </td>
                    <td className="px-3 py-2">
                      <div className="flex items-center justify-end gap-1">
                        {s.status === 'PENDING' && canCollect && (
                          <Button size="sm" onClick={() => handleCollect(s)} disabled={collectSpecimen.isPending}>
                            <CheckCircle size={12} className="mr-1" /> Collect
                          </Button>
                        )}
                        {s.status !== 'COMPLETED' && s.status !== 'CANCELLED' && s.status !== 'REJECTED' && canReject && (
                          <Button variant="danger" size="sm" onClick={() => setRejectTarget(s)}>
                            <XCircle size={12} />
                          </Button>
                        )}
                        {s.status === 'REJECTED' && canRedraw && (
                          <Button size="sm" onClick={() => handleRedraw(s)} disabled={redrawSpecimen.isPending}>
                            <RotateCcw size={12} />
                          </Button>
                        )}
                        {(s.status === 'PENDING' || s.status === 'CANCELLED') && canDelete && (
                          <Button
                            variant="outline"
                            size="sm"
                            className="text-danger hover:bg-danger/10 hover:text-danger"
                            onClick={() => setDeleteTarget(s)}
                            title="Delete specimen"
                          >
                            <Trash2 size={12} />
                          </Button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        <Pagination
          page={page}
          totalPages={meta?.totalPages || 1}
          total={meta?.total || 0}
          limit={limit}
          onPageChange={setPage}
          itemName="specimens"
        />
      </div>

      <SpecimenDetailsModal
        specimenId={detailsId}
        onClose={() => setDetailsId(null)}
        onViewReceipt={(receiptId) => navigate(`/diagnostics/receipts/${receiptId}`)}
      />

      {/* Reject modal */}
      {rejectTarget && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className="bg-surface border border-border rounded-xl shadow-lg w-full max-w-md p-5 space-y-4">
            <h2 className="text-lg font-semibold text-text">Reject specimen</h2>
            <p className="text-sm text-text-muted">
              {rejectTarget.specimenBarcode} — this resets the linked orders for re-draw.
            </p>
            <textarea
              value={rejectReason}
              onChange={(e) => setRejectReason(e.target.value)}
              rows={3}
              placeholder="Reason (e.g. hemolysed, clotted, insufficient)"
              className="w-full px-3 py-2 border border-border rounded-lg bg-background text-text text-sm focus:outline-none focus:ring-2 focus:ring-primary/40 resize-none"
            />
            <div className="flex justify-end gap-2">
              <Button variant="ghost" onClick={() => { setRejectTarget(null); setRejectReason(''); }}>
                Cancel
              </Button>
              <Button
                variant="danger"
                onClick={confirmReject}
                disabled={!rejectReason.trim() || rejectSpecimen.isPending}
              >
                {rejectSpecimen.isPending && <Loader2 size={14} className="mr-1 animate-spin" />}
                Reject
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Delete specimen modal */}
      {deleteTarget && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className="bg-surface border border-border rounded-xl shadow-lg w-full max-w-md p-5 space-y-4">
            <h2 className="text-lg font-semibold text-text">Delete specimen</h2>
            <p className="text-sm text-text-muted">
              {deleteTarget.specimenBarcode} — this removes the tube and frees its tests. Use{' '}
              <span className="font-medium text-text">Generate Specimen Plan</span> on the receipt to
              recreate it (with a new barcode).
            </p>
            <div className="flex justify-end gap-2">
              <Button variant="ghost" onClick={() => setDeleteTarget(null)}>
                Cancel
              </Button>
              <Button variant="danger" onClick={confirmDelete} disabled={deleteSpecimen.isPending}>
                {deleteSpecimen.isPending && <Loader2 size={14} className="mr-1 animate-spin" />}
                Delete
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}